<template>
  <Modal ref="modalRef" width="27.5rem" @after-close="reset">
    <template #header>
      <Text variant="h4">Confirm email change</Text>
    </template>

    <template #body>
      <form @submit.prevent="submit">
        <View direction="column" gap="md">
          <Text variant="body-medium" color="secondary">
            You're changing your sign-in email from
            <b>{{ currentEmail }}</b>
            to
            <b>{{ newEmail }}</b>
            .
          </Text>

          <FormField label="Current password" :error="form.fieldErrors('password')">
            <Input v-model="form.fields.password" type="password" placeholder="Confirm it's you" />
          </FormField>
        </View>
      </form>
    </template>

    <template #footer>
      <Button variant="ghost" size="medium" @click="close">Cancel</Button>
      <Button size="medium" :loading="form.submitting" @click="submit">Send verification link</Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref } from 'vue';

import {
  Button,
  extractErrorMessage,
  FormField,
  Input,
  Modal,
  Text,
  useAlertsStore,
  useForm,
  View,
} from '@tallo/design-system';
import { profileService } from '@tallo/investor/investor';

defineProps<{ currentEmail: string }>();
const emit = defineEmits<{ requested: [pendingEmail: string | null] }>();

const alertsStore = useAlertsStore();

const modalRef = ref<InstanceType<typeof Modal>>();
const newEmail = ref('');

const form = useForm({ password: '' });

function show(requestedEmail: string) {
  newEmail.value = requestedEmail;
  modalRef.value?.show();
}

function close() {
  modalRef.value?.close();
}

function reset() {
  form.fields.password = '';
  form.resetValidation();
}

function submit() {
  form.submit(async () => {
    try {
      const { pendingEmail } = await profileService.requestEmailChange(
        newEmail.value,
        form.fields.password || undefined,
      );
      alertsStore.success(`Verification link sent to <b>${newEmail.value}</b>`);
      emit('requested', pendingEmail);
      close();
    } catch (error) {
      form.setFieldError('password', extractErrorMessage(error, 'Something went wrong. Please try again.'));
    }
  });
}

defineExpose({ show });
</script>
